/**
 * User detail modal component for admin user management.
 */

import * as stylex from "@stylexjs/stylex";
import { useState } from "react";

import { button, form, layout, modal, spacing, text } from "@animus/primaris";

import type { AdminUser, Role } from "./types.ts";
import { styles } from "./styles.stylex.ts";

interface UserDetailModalProps {
  user: AdminUser;
  isAdmin: boolean;
  onClose: () => void;
  onBan: (reason?: string) => void;
  onUnban: () => void;
  onSetRole: (role: Role) => void;
  onDelete: () => void;
}

function UserDetailModal({
  user,
  isAdmin,
  onClose,
  onBan,
  onUnban,
  onSetRole,
  onDelete,
}: UserDetailModalProps) {
  const [banReason, setBanReason] = useState("");

  return (
    <div {...stylex.props(modal.overlay)}>
      <div {...stylex.props(modal.container)}>
        <div {...stylex.props(modal.header)}>
          <h2 {...stylex.props(text.bold, text.xl, text.white)}>
            User Details
          </h2>
          <button
            {...stylex.props(modal.close)}
            onClick={onClose}
            type="button"
          >
            &times;
          </button>
        </div>

        <div {...stylex.props(form.space)}>
          <div {...stylex.props(styles.field)}>
            <div {...stylex.props(text.sm, text.muted)}>Name</div>
            <p {...stylex.props(text.white)}>{user.name || "Unnamed"}</p>
          </div>

          <div {...stylex.props(styles.field)}>
            <div {...stylex.props(text.sm, text.muted)}>Email</div>
            <p {...stylex.props(text.white)}>{user.email}</p>
          </div>

          <div {...stylex.props(styles.field)}>
            <div {...stylex.props(text.sm, text.muted)}>Role</div>
            {isAdmin ? (
              <select
                {...stylex.props(form.input, spacing.mt1)}
                onChange={(e) => onSetRole(e.target.value as Role)}
                value={user.role}
              >
                <option value="user">User</option>
                <option value="moderator">Moderator</option>
                <option value="admin">Admin</option>
              </select>
            ) : (
              <p {...stylex.props(text.white, text.capitalize)}>{user.role}</p>
            )}
          </div>

          <div {...stylex.props(styles.field)}>
            <div {...stylex.props(text.sm, text.muted)}>Status</div>
            <p
              {...stylex.props(
                user.banned ? styles.statusBanned : styles.statusActive
              )}
            >
              {user.banned ? "Banned" : "Active"}
            </p>
            {user.banReason && (
              <p {...stylex.props(text.sm, text.muted)}>
                Reason: {user.banReason}
              </p>
            )}
          </div>

          <div {...stylex.props(styles.field)}>
            <div {...stylex.props(text.sm, text.muted)}>Created</div>
            <p {...stylex.props(text.white)}>
              {new Date(user.createdAt).toLocaleString()}
            </p>
          </div>

          {!user.banned && (
            <div {...stylex.props(styles.field)}>
              <label
                {...stylex.props(text.sm, text.muted)}
                htmlFor="ban-reason-input"
              >
                Ban Reason
              </label>
              <input
                {...stylex.props(form.input, spacing.mt1)}
                id="ban-reason-input"
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Optional reason for banning"
                type="text"
                value={banReason}
              />
            </div>
          )}

          <div {...stylex.props(layout.flexRow, layout.gap2, spacing.pt4)}>
            {user.banned ? (
              <button
                {...stylex.props(button.base, button.success, button.full)}
                onClick={onUnban}
                type="button"
              >
                Unban User
              </button>
            ) : (
              <button
                {...stylex.props(button.base, button.warning, button.full)}
                onClick={() => onBan(banReason || undefined)}
                type="button"
              >
                Ban User
              </button>
            )}
            {isAdmin && (
              <button
                {...stylex.props(button.base, button.danger, button.full)}
                onClick={onDelete}
                type="button"
              >
                Delete User
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { UserDetailModal, type UserDetailModalProps };
